/**
 * GameListAdapter - Fetches game data from the API
 *
 * The database is the single source of truth for all game data including images.
 * Images should be stored as URLs (e.g., Azure Blob Storage URLs) in the database.
 *
 * Caching Strategy:
 * - In-memory cache only (no localStorage) - fresh data on each page load
 * - This ensures game changes (purchases, admin disable/enable) are reflected immediately
 * - Force refresh option available for explicit cache bypass
 *
 * Data Model:
 * - Products (cachedProducts): Purchasable items from GamePack table (store, cart, checkout)
 * - Playable Games (cachedPlayableGames): Individual games from Games table (lobby, ownership)
 *
 * Usage:
 * - Import this instead of GameList.js
 * - Call initializeGames() on app startup
 * - Use getGameList() for store/cart/checkout (returns products)
 * - Use getPlayableGames() for lobby/ownership display (returns games in product shape)
 * - Use findGameById() and findGameBySlug() for lookups
 */

import gamesService from "services/games.service.js";

// In-memory cache for fast access within a session
let cachedProducts = [];
let cachedPlayableGames = [];
let isLoading = false;
let loadError = null;
let isInitialized = false;

// Clean up old localStorage cache (one-time migration)
try {
    localStorage.removeItem('gameListCache');
} catch (e) {
    // Ignore errors
}

/**
 * Clear the in-memory cache
 */
export const clearGamesCache = () => {
    cachedProducts = [];
    cachedPlayableGames = [];
    isInitialized = false;
    console.log('GameListAdapter: Cache cleared');
};

/**
 * Wraps a game from the games[] API response into the product shape
 * so existing components (Game.js, GameVote.js, etc.) work without changes.
 * Maps flat game fields into nested meta/gameInfo sub-objects.
 */
const wrapGameAsProduct = (game) => {
    return {
        // Identity
        gameId: game.gameId,
        name: game.name,
        gameServerName: game.gameServerName,
        status: game.status,
        description: game.description,
        // Gameplay
        logo: game.logo,
        gameUrl: game.gameUrl,
        iosGameId: game.iosGameId,
        androidGameId: game.androidGameId,
        supportedPlatforms: game.supportedPlatforms || [],
        gameShow: game.gameShow,
        partyGame: game.partyGame,
        tags: game.tags || [],
        // Nested objects matching product shape
        gameInfo: {
            minPlayers: game.minPlayers,
            maxPlayers: game.maxPlayers,
            avgRoundLengthText: game.avgRoundLengthText,
            minAge: game.minAge,
            voiceEnabled: game.voiceEnabled,
            lobbyBanner: game.lobbyBanner,
            lobbyCornerBanner: game.lobbyCornerBanner,
        },
        meta: {
            gameCard: game.gameCard,
            tinyCard: game.tinyCard,
            gameMenuTextColour: game.gameMenuTextColour,
            gameMenuBackgroundColour: game.gameMenuBackgroundColour,
            gameMenuBackground: game.gameMenuBackground,
            iconTheme: game.iconTheme,
            ageRatingIcon: game.ageRatingIcon,
            previewImages: game.previewImages,
        },
    };
};

/**
 * Initialize the game list from API
 * Call this on app startup (e.g., in App.js or index.js)
 * @param {boolean} forceRefresh - Force refresh from API even if cached in memory
 * @param {string} [region] - ISO country code (US/AU/GB) used by the server to
 *   resolve regional tag scoping (e.g. which games show as best-sellers in
 *   the user's country). Pass pricing.countryCode from Redux. Safe to omit
 *   on the first call — server will fall back to default-region applications.
 *   Re-call with forceRefresh once countryCode lands to upgrade the list.
 * @returns {Promise<Array>} The product list (for backwards compatibility)
 */
export const initializeGames = async (forceRefresh = false, region = undefined) => {
    // Return in-memory cache if already initialized and not forcing refresh
    if (isInitialized && cachedProducts.length > 0 && !forceRefresh) {
        return cachedProducts;
    }

    // Wait for existing load to complete
    if (isLoading) {
        return new Promise((resolve) => {
            const checkInterval = setInterval(() => {
                if (!isLoading) {
                    clearInterval(checkInterval);
                    resolve(cachedProducts);
                }
            }, 100);
        });
    }

    // Fetch from API
    isLoading = true;
    loadError = null;

    // Retry transient failures. An "empty lobby after login" report usually
    // traces back to a single CMS blip on app boot — without retry the host
    // gets stuck with `cachedPlayableGames = []` and `getOwnedGames()`
    // intersects the user's owned IDs against an empty catalog. Match the
    // pattern used by Lobby.getUserGames so behaviour stays predictable.
    const BACKOFFS_MS = [0, 1500, 3000];

    try {
        let apiResponse = null;
        let lastError = null;
        for (let attempt = 0; attempt < BACKOFFS_MS.length; attempt++) {
            if (BACKOFFS_MS[attempt] > 0) {
                await new Promise(r => setTimeout(r, BACKOFFS_MS[attempt]));
            }
            try {
                apiResponse = await gamesService.getGames(region);
                lastError = null;
                break;
            } catch (err) {
                lastError = err;
                console.warn(`GameListAdapter: attempt ${attempt + 1}/${BACKOFFS_MS.length} failed:`, err?.message || err);
            }
        }
        if (lastError) throw lastError;

        // Handle new { games, products } response or legacy flat array
        let rawProducts;
        let rawGames;

        if (apiResponse && apiResponse.products && Array.isArray(apiResponse.products)) {
            // New structured response
            rawProducts = apiResponse.products;
            rawGames = apiResponse.games || [];
        } else if (Array.isArray(apiResponse)) {
            // Legacy flat array response (backwards compatibility)
            rawProducts = apiResponse;
            rawGames = [];
        } else {
            console.warn('GameListAdapter: Unexpected API response format');
            rawProducts = [];
            rawGames = [];
        }

        // Filter and deduplicate products
        // In dev (local + devdeploy) we keep products that don't yet have a Stripe
        // productId so WIP items show up in the storefront for preview. Production
        // still drops them — checkout requires a Stripe product.
        const isDev = !process.env.REACT_APP_ENVIRONMENT || process.env.REACT_APP_ENVIRONMENT === 'development';
        const seenProducts = new Set();
        const validProducts = rawProducts.filter(game => {
            if (!game.gameId) {
                console.warn(`GameListAdapter: Skipping product with missing gameId: ${game.name || 'unnamed'}`);
                return false;
            }
            if (!game.productId) {
                if (!isDev) {
                    console.warn(`GameListAdapter: Skipping product with missing productId: ${game.gameId}`);
                    return false;
                }
                console.warn(`GameListAdapter: Product ${game.gameId} has no productId — allowed in dev for preview, would be skipped in prod`);
            }
            if (seenProducts.has(game.gameId)) {
                console.warn(`GameListAdapter: Duplicate product gameId found: ${game.gameId}`);
                return false;
            }
            seenProducts.add(game.gameId);
            return true;
        });

        // Filter and deduplicate playable games
        const seenGames = new Set();
        const validGames = rawGames.filter(game => {
            if (!game.gameId) {
                console.warn(`GameListAdapter: Skipping game with missing gameId: ${game.name || 'unnamed'}`);
                return false;
            }
            if (seenGames.has(game.gameId)) {
                console.warn(`GameListAdapter: Duplicate game gameId found: ${game.gameId}`);
                return false;
            }
            seenGames.add(game.gameId);
            return true;
        });

        cachedProducts = validProducts;
        cachedPlayableGames = validGames;
        isInitialized = true;

        console.log(`GameListAdapter: Loaded ${cachedProducts.length} products and ${cachedPlayableGames.length} playable games from API`);
        return cachedProducts;
    } catch (error) {
        console.error('GameListAdapter: Failed to load games from API', error);
        loadError = error;
        cachedProducts = [];
        cachedPlayableGames = [];
        return cachedProducts;
    } finally {
        isLoading = false;
    }
};

/**
 * Force refresh games from API, bypassing in-memory cache
 * @param {string} [region] - ISO country code (US/AU/GB) for regional tag
 *   scoping. Typically pricing.countryCode from Redux. See initializeGames.
 * @returns {Promise<Array>} The refreshed product list
 */
export const refreshGames = async (region = undefined) => {
    isInitialized = false;
    return initializeGames(true, region);
};

/**
 * Get the current product list (for store, cart, checkout, hero sections)
 * Returns cached products from API
 * @returns {Array} Array of product objects
 */
export const getGameList = () => {
    return cachedProducts;
};

/**
 * Get the playable games list (for lobby, ownership display, game selection)
 * Returns games wrapped into the product shape so existing components work unchanged.
 * @returns {Array} Array of game objects in product shape
 */
export const getPlayableGames = () => {
    return cachedPlayableGames.map(wrapGameAsProduct);
};

/**
 * Find a playable game by its gameId
 * @param {string} gameId - The game identifier
 * @returns {Object|undefined} The game object in product shape, or undefined
 */
export const findPlayableGameById = (gameId) => {
    const game = cachedPlayableGames.find(g => g.gameId === gameId);
    return game ? wrapGameAsProduct(game) : undefined;
};

/**
 * Find a product by its gameId (searches products only - for store/checkout context)
 * @param {string} gameId - The game identifier
 * @returns {Object|undefined} The product object or undefined
 */
export const findGameById = (gameId) => {
    return cachedProducts.find(g => g.gameId === gameId);
};

/**
 * Find a game by its store slug
 * @param {string} slug - The store slug (with or without /shop/ prefix)
 * @returns {Object|undefined} The game object or undefined
 */
export const findGameBySlug = (slug) => {
    const normalizedSlug = slug.startsWith('/shop/') ? slug : `/shop/${slug}`;
    // Addons are surfaced as cards on their parent game's page, never as a
    // destination of their own. Skipping them here ensures /shop/<slug> always
    // resolves to the parent product even when an addon shares the same slug.
    return cachedProducts.find(g => g.storeSlug === normalizedSlug && g.type !== 'Addon');
};

/**
 * Check if a game is enabled (status === "enabled")
 */
const isEnabled = (g) => g.status === "enabled";

/**
 * Get enabled games only
 * @returns {Array} Array of enabled product objects
 */
export const getEnabledGames = () => {
    return cachedProducts.filter(isEnabled);
};

/**
 * Get games marked as best sellers
 * @returns {Array} Array of best seller product objects
 */
export const getBestSellers = () => {
    return cachedProducts.filter(g => g.bestSeller && isEnabled(g));
};

/**
 * Get games marked as game shows
 * @returns {Array} Array of game show product objects
 */
export const getGameShows = () => {
    return cachedProducts.filter(g => g.gameShow && isEnabled(g));
};

/**
 * Get party games
 * @returns {Array} Array of party game product objects
 */
export const getPartyGames = () => {
    return cachedProducts.filter(g => g.partyGame && isEnabled(g));
};

/**
 * Get all addons
 * @returns {Array} Array of addon product objects
 */
export const getAddons = () => {
    return cachedProducts.filter(g => g.type === 'Addon');
};

/**
 * Get addons for a specific game
 * @param {string} gameId - The base game identifier
 * @returns {Array} Array of addon objects for that game
 */
export const getAddonsForGame = (gameId) => {
    return cachedProducts.filter(g => g.type === 'Addon' && g.addonIds?.includes(gameId));
};

/**
 * Returns the addon with parent-Game meta filled in for any empty asset slots.
 * Lobby quick-buy, in-lobby checkout modal, etc. surface addon cards via the
 * generic Game component, which reads meta.gameCard / meta.storeCard / etc.
 * Addons that aren't yet fully configured would otherwise render with the
 * placeholder card; this helper transparently borrows the parent's visuals.
 * No-op for non-Addon products.
 *
 * @param {object} product - product from the catalog (any type)
 * @returns {object} the same product, or a shallow copy with merged meta
 */
export const enrichAddonWithParentMeta = (product) => {
    if (!product || product.type !== 'Addon') return product;
    const parent = cachedProducts.find(
        p => p.type === 'Game'
            && Array.isArray(p.addonIds)
            && p.addonIds.includes(product.gameId)
    );
    if (!parent) return product;
    const pmeta = parent.meta || {};
    const ameta = product.meta || {};
    const filled = {};
    Object.keys(ameta).forEach(k => {
        if (ameta[k] !== '' && ameta[k] !== null && ameta[k] !== undefined) filled[k] = ameta[k];
    });
    return { ...product, meta: { ...pmeta, ...filled } };
};

/**
 * Get sellable addons attached to a product, decorated with ownership.
 * Filters to status === 'enabled' so hidden/disabled addons stay off the shop.
 * Pass `includeHidden` to also surface 'hidden' status addons (for dev visibility);
 * those items come back with isHidden=true so the caller can grey them out.
 * @param {object} game - The parent product (must have addonIds)
 * @param {string[]} ownedAddonGameIds - Addon gameIds the current user owns
 * @param {{ includeHidden?: boolean }} [options]
 * @returns {Array} Array of addon objects with isOwned + isHidden flags
 */
export const getAddonsForSale = (game, ownedAddonGameIds = [], { includeHidden = false } = {}) => {
    if (!game?.addonIds?.length) return [];
    const ownedSet = new Set(ownedAddonGameIds);
    const allowedStatuses = includeHidden ? ['enabled', 'hidden'] : ['enabled'];
    return cachedProducts
        .filter(g => g.type === 'Addon' && allowedStatuses.includes(g.status) && game.addonIds.includes(g.gameId))
        .map(g => ({ ...g, isOwned: ownedSet.has(g.gameId), isHidden: g.status === 'hidden' }));
};

/**
 * Get all game packs
 * @returns {Array} Array of game pack product objects
 */
export const getGamePacks = () => {
    return cachedProducts.filter(g => g.type === 'GamePack');
};

/**
 * Check if games have been loaded from API
 * @returns {boolean}
 */
export const isLoadedFromApi = () => {
    return isInitialized && loadError === null;
};

/**
 * Check if games are currently loading
 * @returns {boolean}
 */
export const isGamesLoading = () => isLoading;

/**
 * Get the load error if any
 * @returns {Error|null}
 */
export const getLoadError = () => loadError;

// Default export - the product list getter
export default cachedProducts;
